import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';

/**
 * Seat management (#609, Phase 1) read hooks.
 *
 * Two partner-scoped, SECURITY-DEFINER read RPCs:
 *  - `partner_seat_usage`  → seat count vs any contracted licensed cap.
 *  - `partner_member_list` → every distinct member of the partner (manual +
 *    SSO), so manually-provisioned members (which have no SSO identity) still
 *    appear on the Members page. `enterprise_emulation_member_list` only shows
 *    emulatable (SSO-identity-backed) members, so we read the broader list here.
 *
 * Both RPCs re-compute authorization server-side
 * (`caller_is_partner_admin_or_super(partner_id)`) — these hooks are a thin data
 * layer, not the security boundary.
 */

/** partner_seat_usage result. */
export interface SeatUsage {
  /** NULL = unconfigured (uncapped); 'per_seat' = uncapped; 'licensed' = capped. */
  billing_model: 'licensed' | 'per_seat' | null;
  /** Contracted seat cap; only meaningful (and rendered) when billing_model='licensed'. */
  licensed_seats: number | null;
  /** Distinct active members across the partner's workspaces. */
  seats_used: number;
  /** True only when licensed + at/over the cap. */
  at_cap: boolean;
}

/** partner_member_list row (one per distinct member). */
export interface PartnerMember {
  user_id: string;
  email: string;
  last_sign_in_at: string | null;
  /**
   * Canonical is_emulatable_member: has an active SSO identity for this partner
   * AND is not a partner admin / super_admin. Matches what
   * enterprise_emulation_authorize actually allows, so the Emulate action is
   * only offered for members who can really be emulated.
   */
  is_emulatable: boolean;
  /** Manually provisioned (no active SSO identity yet) → cannot be emulated. */
  is_manual: boolean;
}

export function useSeatUsage(partnerId: string) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.seatUsage(partnerId),
    queryFn: async (): Promise<SeatUsage> => {
      const { data, error } = await supabase.rpc('partner_seat_usage', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return data as SeatUsage;
    },
    enabled: !!partnerId,
  });
}

export function usePartnerMemberList(partnerId: string) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.memberList(partnerId),
    queryFn: async (): Promise<PartnerMember[]> => {
      const { data, error } = await supabase.rpc('partner_member_list', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as PartnerMember[];
    },
    enabled: !!partnerId,
  });
}

/** partner_workspace_seat_breakdown row (one per workspace). */
export interface WorkspaceSeatRow {
  workspace_id: string;
  workspace_name: string;
  /** Active members in THIS workspace — NOT deduped across the partner. */
  seats: number;
  created_at: string;
}

export function usePartnerWorkspaceBreakdown(partnerId: string) {
  return useQuery({
    queryKey: queryKeys.enterprisePartners.workspaceSeatBreakdown(partnerId),
    queryFn: async (): Promise<WorkspaceSeatRow[]> => {
      const { data, error } = await supabase.rpc('partner_workspace_seat_breakdown', {
        p_partner_id: partnerId,
      });
      if (error) throw error;
      return (data ?? []) as WorkspaceSeatRow[];
    },
    enabled: !!partnerId,
  });
}
